/**
 * `upsertScenePreset` (WP-1).
 *
 * Pure controller for the Save-scene-preset dialog. Captures the optimistic
 * UI -> rollback contract masterplan §5.4 calls for, **separately from**
 * the React component, so the rollback semantics can be unit-tested
 * end-to-end without rendering a DOM.
 *
 * Behaviour:
 *   1. Calls `optimistic.apply()` to immediately reflect the new preset in
 *      the local store (so the saved-views panel updates instantly).
 *   2. Calls `gtwinClient.scene.presets.upsert(...)` via the WP-1 adapter.
 *   3. On success: returns the persisted preset (and emits an audit row
 *      via the adapter's mock).
 *   4. On failure: invokes `optimistic.rollback()` and re-throws the
 *      original error so the caller can render an error banner.
 */

import type {
  ScenePresetInput,
  ScenePresetsAdapter,
  SceneSlot,
} from '@/sdk-stub/gtwin-sdk';

export interface OptimisticHandle<T> {
  apply(value: T): void;
  rollback(): void;
}

export interface ScenePresetCaptureInput {
  readonly id: string;
  readonly label: string;
  readonly show: ReadonlyArray<SceneSlot>;
  readonly layerVisibility?: Readonly<Record<string, boolean>>;
  readonly layerOpacity?: Readonly<Record<string, number>>;
  readonly camera?: ScenePresetInput['camera'];
  readonly time?: ScenePresetInput['time'];
  readonly description?: string;
}

export interface UpsertScenePresetDeps {
  readonly gtwinId: string;
  readonly preset: ScenePresetCaptureInput;
  readonly adapter: ScenePresetsAdapter;
  readonly optimistic: OptimisticHandle<ScenePresetInput>;
  /** Optional follow-up; default `adapter.activate(gtwinId, preset.id)`. */
  readonly onActivate?: (gtwinId: string, presetId: string) => Promise<void>;
  /** Optional logger; default no-op. */
  readonly onAudit?: (entry: { action: string; presetId: string; existed: boolean }) => void;
}

export interface UpsertScenePresetResult {
  readonly preset: ScenePresetInput;
  readonly activated: boolean;
}

export async function upsertScenePreset(
  deps: UpsertScenePresetDeps,
): Promise<UpsertScenePresetResult> {
  const input: ScenePresetInput = {
    id: deps.preset.id,
    label: deps.preset.label,
    show: deps.preset.show,
    layerVisibility: deps.preset.layerVisibility,
    layerOpacity: deps.preset.layerOpacity,
    camera: deps.preset.camera,
    time: deps.preset.time,
    description: deps.preset.description,
  };

  deps.optimistic.apply(input);

  let persisted: ScenePresetInput;
  try {
    persisted = await deps.adapter.upsert(deps.gtwinId, input);
  } catch (err) {
    deps.optimistic.rollback();
    throw err;
  }

  let activated = false;
  try {
    if (deps.onActivate) {
      await deps.onActivate(deps.gtwinId, persisted.id);
    } else {
      await deps.adapter.activate(deps.gtwinId, persisted.id);
    }
    activated = true;
  } catch (err) {
    // Activation is best-effort — the preset is persisted, but the active
    // pointer may need a manual click. The caller surfaces the warning.
    activated = false;
    if (typeof console !== 'undefined') {
      console.warn(
        `[upsertScenePreset] Preset persisted but activate failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }

  deps.onAudit?.({
    action: 'scene.presets.upsert',
    presetId: persisted.id,
    existed: false,
  });

  return { preset: persisted, activated };
}
